<template>
  <g-g-app-modal
    v-if="!getAnswered"
    :is-active="modelValue"
    @close="close"
    width="600px"
    :persistent="true"
    :close-button-positions="['header']"
    :footer-close-attrs="{ color: 'primary', text: true }"
  >
    <template v-slot:header>
      <div style="text-align: start;" class="mr-auto ml-auto" v-if="step != 0">
        {{ getSurvey.title }}
      </div>
    </template>

    <template v-slot:body>
      <app-survey-stepper
        v-model="step"
        :steps="stepsCount"
        :hide-controls-for-steps="[0]"
        :loading="isSending"
        @change="(newStep) => { step = newStep; }"
        @finish="sendSurvey"
      >
        <template>
          <app-survey-step
            :step="0"
            class="pt-5 d-flex flex-column align-items-center"
          >
            <welcoming-step
              :title="getSurvey.title || ''"
              :description="getSurvey.description || ''"
              @start="step = 1"
              @skip="close"
            />
          </app-survey-step>

          <app-survey-step
            v-for="(item, key) in getSurvey.questions"
            :key="`step-${key}`"
            :step="key + 1"
            class="py-6 d-flex flex-column align-items-center"
          >
            <v-row style="width: 100%;">
              <v-col cols="12">
                <label class="h4 mb-4 font-weight-bold">{{ item.prompt }}</label>
              </v-col>
              <v-col
                v-if="item.type === 'select'"
                cols="12"
                class="py-0"
              >
                <gg-select
                  :key="`select-${key}`"
                  outlined
                  placeholder="Select an option"
                  v-model="data.selected[item.id]"
                  item-value="id"
                  item-text="label"
                  :items="item.options"
                  v-validate="'required'"
                  :data-vv-name="`select-${item.id}`"
                  :error-messages="errors.collect(`select-${item.id}`)"
                  data-vv-as="Field"
                  :disabled="isSending"
                />
              </v-col>
              <v-col
                v-else-if="item.type === 'select' && item.options.find((i) => i.id == data.selected[item.id]) && item.options.find((i) => i.id == data.selected[item.id]).label === 'Other'"
                cols="12"
                class="py-0"
              >
                <gg-text-field
                  v-model="data.text[item.id]"
                  :key="`text-${key}`"
                  label="In more detail"
                  outlined
                  v-validate="'required'"
                  :data-vv-name="`text-${item.id}`"
                  data-vv-as="Field for Other option"
                  :error-messages="errors.collect(`text-${item.id}`)"
                  :disabled="isSending"
                />
              </v-col>
              <v-col
                v-else-if="item.type === 'select-multi'"
                cols="12"
                class="py-0"
              >
                <gg-select
                  v-model="data.selected[item.id]"
                  :key="`multi-${key}`"
                  outlined
                  multiple
                  :ref="`multi-${key}`"
                  placeholder="Select one or more options"
                  item-value="id"
                  item-text="label"
                  :items="item.options"
                  v-validate="'required'"
                  :data-vv-name="`select-${item.id}`"
                  data-vv-as="Field"
                  :error-messages="errors.collect(`select-${item.id}`)"
                  :disabled="isSending"
                  :menu-props="menuPropsFor(key)"
                  @focus="syncSelectMenuWidth(key)"
                />
              </v-col>
              <v-col
                v-else-if="item.type === 'select-multi'
                  && data.selected
                  && data.selected[item.id]
                  && data.selected[item.id].length
                  && item.options.find((i) => i.label == 'Other')
                  && data.selected[item.id].includes(item.options.find((i) => i.label == 'Other').id)"
                cols="12"
                class="py-0"
              >
                <gg-text-field
                  v-model="data.text[item.id]"
                  :key="`text-${key}`"
                  label="Since you selected Other, enter your own response:"
                  outlined
                  placeholder="Enter something not on our list"
                  v-validate="'required'"
                  :data-vv-name="`text-${item.id}`"
                  data-vv-as="Field for Other option"
                  :error-messages="errors.collect(`text-${item.id}`)"
                  :disabled="isSending"
                />
              </v-col>
              <v-col
                v-else-if="item.type === 'text'"
                cols="12"
                class="py-0"
              >
                <v-textarea
                  v-model="data.text[item.id]"
                  :key="`textarea-${key}`"
                  outlined
                  class="gg-input"
                  dense
                  v-validate="'required'"
                  :data-vv-name="`field-${item.id}`"
                  data-vv-as="Field"
                  :error-messages="errors.collect(`field-${item.id}`)"
                  :disabled="isSending"
                >
                </v-textarea>
              </v-col>
              <v-col v-else-if="item.type === 'select-radio'">
                <v-radio-group
                  v-model="data.selected[item.id]"
                  :key="`radio-${key}`"
                  :row="false"
                  class="mt-0"
                  v-validate="'required'"
                  :data-vv-name="`radio-${item.id}`"
                  data-vv-as="Field"
                  :error-messages="errors.collect(`radio-${item.id}`)"
                  :disabled="isSending"
                  hide-details="auto"
                >
                  <v-radio
                    v-for="option in item.options"
                    :key="option.id"
                    :value="option.id"
                    class="mt-0"
                  >
                    <template v-slot:label>
                        <b>{{ option.label }}</b>
                        <span v-show="option.description" class="text-grey ml-2">{{ option.description }}</span>
                    </template>
                  </v-radio>
                </v-radio-group>
              </v-col>
              <v-col v-else-if="item.type === 'select-iconboxes' || item.type === 'select-iconboxes-multi'" class="pt-0">
                <g-g-buttons-list-group
                  v-model="data.selected[item.id]"
                  :key="`iconboxes-${key}`"
                  item-value-key="id"
                  :data-vv-name="`iconboxes-${item.id}`"
                  v-validate="'required'"
                  data-vv-as="Field"
                  :error-messages="errors.collect(`iconboxes-${item.id}`)"
                  :multi="item.type === 'select-iconboxes-multi'"
                >
                  <template v-slot:default="{ isActive, handleClick }">
                    <template v-if="item.size === 'large'">
                      <g-g-card-btn
                        v-for="option in item.options"
                        :key="`iconbox-${option.id}`"
                        :disabled="isSending"
                        :icon="option.icon ? `mdi-${option.icon}` : 'mdi-star'"
                        :label="option.label"
                        :description="option.description"
                        :class="{'active': isActive(option.id)}"
                        @click="handleClick(option.id)"
                      />
                    </template>
                    <template v-else>
                      <gg-btn
                        v-for="option in item.options"
                        :key="`iconbox-${option.id}`"
                        :secondary="!isActive(option.id)"
                        @click="handleClick(option.id)"
                        :disabled="isSending"
                      >
                        <v-icon v-show="option.icon" class="mr-2" left size="1.2rem">
                          {{ option.icon ? `mdi-${option.icon}` : '' }}
                        </v-icon>
                        {{ option.label }}
                      </gg-btn>
                    </template>
                  </template>
                </g-g-buttons-list-group>
              </v-col>
            </v-row>
          </app-survey-step>
        </template>

        <template v-slot:controls="{ nextStep, previousStep, isFirstStep, isLastStep, hideControlsForSteps, steps }">
          <gg-btn text :disabled="isFirstStep || isSending" @click="previousStep">
            <v-icon left small>mdi-chevron-left</v-icon>
            Back
          </gg-btn>
          <span>
            Step {{ step + 1 - hideControlsForSteps.length }} of {{ steps - hideControlsForSteps.length }}
          </span>
          <gg-btn v-show="!isLastStep" text :disabled="isSending" @click="validateBeforeNextStep(nextStep)">
            Next
            <v-icon right small>mdi-chevron-right</v-icon>
          </gg-btn>
          <gg-btn v-show="isLastStep" :loading="isSending" @click="sendSurvey">
            Finish
          </gg-btn>
        </template>
      </app-survey-stepper>

    </template>
  </g-g-app-modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import GGAppModal from '../Elements/GGAppModal.vue'
import GGButtonsListGroup from '@/components/Elements/GGButtonsListGroup'
import AppSurveyStepper from '@/components/AppSurvey/AppSurveyStepper'
import AppSurveyStep from '@/components/AppSurvey/AppSurveyStep'
import WelcomingStep from '@/components/AppSurvey/WelcomingStep'
import GGCardBtn from '@/components/Elements/GGCardBtn'

// Question types: 'select'|'select-multi'|'select-radio'|'select-iconboxes'|'select-iconboxes-multi'
export default {
  name: 'AppSurvey',
  components: {
    GGAppModal,
    GGButtonsListGroup,
    AppSurveyStepper,
    AppSurveyStep,
    WelcomingStep,
    // eslint-disable-next-line vue/no-unused-components
    GGCardBtn,
  },

  data () {
    return {
      step: 0,
      modelValue: true,
      isSending: false,
      data: {
        selected: {},
        text: {},
      },
      menuWidths: {}, // { [key]: px }
    }
  },

  computed: {
    ...mapGetters(['getSurvey', 'getAnswered']),
    stepsCount () {
      // +1 because step 0 is the WelcomingStep
      return (this.getSurvey.questions || []).length + 1
    },
  },

  methods: {
    ...mapActions(['respondSurvey']),
    async validateBeforeNextStep (callback) {
      const isValid = await this.$validator.validateAll()
      if (isValid && typeof callback === 'function') {
        callback()
      }
    },
    async sendSurvey () {
      const isValid = await this.$validator.validateAll()
      if (!isValid) {
        return
      }
      const answers = []
      const { selected, text } = this.data
      Object.keys(selected).forEach((questionId) => {
        if (!this.data.selected[questionId]) {
          return
        }
        answers.push({ question_id: questionId, selected: this.data.selected[questionId].length ? this.data.selected[questionId] : [ this.data.selected[questionId] ] })
      })
      Object.keys(text).forEach((questionId) => {
        if (!this.data.text[questionId]) {
          return
        }
        const answerIndex = answers.findIndex((value) => {
          return value.question_id === questionId
        })
        if (answerIndex === -1) {
          answers.push({ question_id: questionId, text: this.data.text[questionId] })
          return
        }
        Object.assign(answers[answerIndex], { text: this.data.text[questionId] })
      })
      this.isSending = true
      const response = await this.respondSurvey({ surveyresponse: answers })
      if (response && (response.result === 'success')) {
        this.close()
      }
      this.isSending = false
    },
    close () {
      this.modelValue = false
      this.data = {
        selected: {},
        text: {},
      }
    },
    // fix v-menu's max-width:
    menuPropsFor (key) {
      const mw = this.menuWidths[key] || 500
      return { 'content-class': 'survey-select-menu-2-cols', maxWidth: mw }
    },
    syncSelectMenuWidth (key) {
      this.$nextTick(() => {
        const ref = this.$refs[`multi-${key}`]
        const el = ref && (ref.$el || Array.isArray(ref) ? ref[0].$el : null) // gg-select.$el exists
        if (!el) return
        const rect = el.getBoundingClientRect()
        if (rect && rect.width) {
          this.$set(this.menuWidths, key, Math.round(rect.width))
        }
      })
    },
  },
  mounted () {
    this.modelValue = true
    // initial measurement for all selects
    this.$nextTick(() => {
      Object.keys(this.getSurvey.questions || {}).forEach((_, i) => {
        const key = i
        this.syncSelectMenuWidth(key)
      })
    })
  }
}
</script>

<style>
.survey-select-menu-2-cols .v-select-list {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  padding: 0;
}

.survey-select-menu-2-cols .v-list > .v-list-item,
.survey-select-menu-2-cols .v-list .v-list-item {
  flex: 0 0 50%;
  max-width: 50%;
  min-width: 0;
  box-sizing: border-box;
  padding-right: 8px;
}

.survey-select-menu-2-cols .v-list .v-list-item {
  padding-top: 6px;
  padding-bottom: 6px;
}

.iconboxes-row {
  display: flex !important;
  flex-wrap: wrap;
  gap: 8px;
  align-items: stretch;
  padding: 8px 0;
}
</style>
